// src/components/Header.jsx

import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Header.module.css';


import api, { extendLogin } from '../api';
import { isTokenValid, getAccessToken, getRefreshToken } from '../auth';
import { registerPush } from '../utils/push';


const ALARM_POLL_MS = 30000;

export default function Header() {
  const navigate = useNavigate();

  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  const [useRefreshClock, setUseRefreshClock] = useState(false);

  const authed =
    isTokenValid(getAccessToken()) || isTokenValid(getRefreshToken());

  const [unreadCount, setUnreadCount] = useState(0);
  const [approvalUnreadCount, setApprovalUnreadCount] = useState(0);  // ✅ 추가


  const [showAlarms, setShowAlarms]   = useState(false);
  
  
  const [alarms, setAlarms]     = useState([]);
  const [alarmTab, setAlarmTab] = useState("unread");


  useEffect(() => {
    if (!authed) return;
    registerPush(api).catch(console.error);
  }, [authed]);

  // 안읽은 알림 개수 — 30초마다 한 번(업무+결재를 한 API로, 2026-10-15).
  // 탭이 숨겨지면 멈추고, 다시 보이면 바로 한 번 부른 뒤 30초 주기를 다시 시작한다.
  // (예전엔 5초마다 2개 API — 전체 API 요청의 약 85%가 이 폴링이었다.)
  useEffect(() => {
    if (!authed) return;
    let timer = null;
    const fetchAlarms = async () => {
      try {
        const res = await api.get("/tasks/notifications/unread-counts/");
        setUnreadCount(res.data.tasks);
        setApprovalUnreadCount(res.data.approvals);
      } catch {}
    };
    const start = () => {
      if (timer) return;
      fetchAlarms();
      timer = setInterval(fetchAlarms, ALARM_POLL_MS);
    };
    const stop = () => {
      clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [authed]);



  const handleBellClick = async () => {
    setShowAlarms((prev) => !prev);
    if (!showAlarms) {
      try {
        const res1 = await api.get("/tasks/notifications/");
        const res2 = await api.get("/draft/approval-notifications/");
        // ✅ 합쳐서 최신순 정렬
        const merged = [
          ...res1.data.map(a => ({ ...a, source: "task" })),
          ...res2.data.map(a => ({ ...a, source: "approval" })),
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));


        setAlarms(merged);




      } catch {}
    }
  };



  const getExpMs = (jwt) => {
    if (!jwt) return 0;
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      return (payload.exp || 0) * 1000;
    } catch {
      return 0;
    }
  };

  const getAccessExpiry  = () => getExpMs(localStorage.getItem('accessToken'));
  const getRefreshExpiry = () => getExpMs(localStorage.getItem('refreshToken'));

  const updateRemainingTime = () => {
    const expiry = useRefreshClock ? getRefreshExpiry() : getAccessExpiry();
    const diff = expiry - Date.now();
    if (diff <= 0) {
      setHours(0); setMinutes(0); setSeconds(0);
      return;
    }
    const total = Math.floor(diff / 1000);
    setHours(Math.floor(total / 3600));
    setMinutes(Math.floor((total % 3600) / 60));
    setSeconds(total % 60);
  };

  const handleExtendSession = async () => {
    console.log('[UI] 로그인 연장 클릭');
    try {
      await extendLogin();
      setUseRefreshClock(true);
      updateRemainingTime();
      alert('로그인 시간이 12시간으로 연장되었습니다.');
    } catch (err) {
      console.error('세션 연장 실패:', err);
      alert('세션을 연장할 수 없습니다. 다시 로그인해주세요.');
      navigate('/login', { replace: true });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete api.defaults.headers.common['Authorization'];
    setUseRefreshClock(false);
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!authed) return;
    updateRemainingTime();
    const id = setInterval(updateRemainingTime, 1000);
    return () => clearInterval(id);
  }, [authed, useRefreshClock]);

  const timeText =
    `${String(hours).padStart(2, '0')}시간 ` +
    `${String(minutes).padStart(2, '0')}분 ` +
    `${String(seconds).padStart(2, '0')}초`;

  return (
    <header className={styles.header}>
      <Link to="/" className={styles.logo}>
        금천일자리주식회사
      </Link>

      <div className={styles.right}>
        {authed ? (
          <nav className={styles.authNav}>
            <div className={styles.timerBox}>
              <span className={styles.clockIcon} aria-hidden>🕒</span>
              <span className={styles.timeText}>{timeText}</span>
            </div>

            <span className={styles.divider}>|</span>

            {/* ✅ 알람 벨 */}
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                type="button"
                className={styles.authLink}
                onClick={handleBellClick}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>



                {(unreadCount + approvalUnreadCount) > 0 && (
                  <span style={{
                    background: "#e74c3c", color: "#fff",
                    borderRadius: "50%", fontSize: "11px",
                    padding: "1px 5px", marginLeft: "2px"
                  }}>
                    {unreadCount + approvalUnreadCount}
                  </span>
                )}



              </button>
              {showAlarms && (

                <div style={{
                  position: "absolute", right: 0, top: "36px",
                  background: "#fff", border: "1px solid #d0d7de",
                  borderRadius: "10px",
                  width: window.innerWidth <= 1024 ? "calc(100vw - 16px)" : "420px",
                  left: window.innerWidth <= 1024 ? "50%" : "auto",
                  transform: window.innerWidth <= 1024 ? "translateX(-50%)" : "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 999,
                  maxHeight: "360px", overflowY: "auto"
                }}>



                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #eee", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <span style={{ fontWeight: 700, fontSize: "13px" }}>알람</span>
                    <div style={{ display:"flex", gap:4 }}>

                      {["unread","all"].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setAlarmTab(tab)}
                          style={{
                            padding:"3px 10px", borderRadius:6, fontSize:11, cursor:"pointer",
                            minWidth:"52px", whiteSpace:"nowrap",          
                            border: alarmTab === tab ? "1px solid #25b765" : "1px solid #d0d7de",
                            background: alarmTab === tab ? "#e8f5ea" : "#fff",
                            color: alarmTab === tab ? "#1a6b1a" : "#555",
                            fontWeight: alarmTab === tab ? 700 : 400,
                          }}
                        >
                          {tab === "unread" ? "안읽음" : "전체"}
                        </button>
                      ))}


                    </div>
                  </div>


                  {(alarmTab === "unread" ? alarms.filter(a => !a.is_read) : alarms).length === 0 ? (

                    <div style={{ padding: "20px", textAlign: "center", color: "#aaa", fontSize: "13px" }}>
                      알람이 없습니다
                    </div>
                  ) : (alarmTab === "unread" ? alarms.filter(a => !a.is_read) : alarms).map((a) => (

                    <div
                      key={a.id}

                      onClick={async () => {
                        if (!a.is_read) {
                          if (a.source === "approval") {
                            await api.patch(`/draft/approval-notifications/${a.id}/read/`);
                          } else {
                            await api.patch(`/tasks/notifications/${a.id}/read/`);
                          }
                          setAlarms((prev) =>
                            prev.map((n) => n.id === a.id && n.source === a.source ? { ...n, is_read: true } : n)
                          );
                          setUnreadCount((prev) => a.source === "task" ? Math.max(0, prev - 1) : prev);
                          setApprovalUnreadCount((prev) => a.source === "approval" ? Math.max(0, prev - 1) : prev);
                        }
                        setShowAlarms(false);

                        if (a.ntype === "task_assigned") {
                          navigate("/task/received/list", { state: { openTaskId: a.task } });

                        } else if (a.ntype === "task_completed") {
                          navigate("/task/assign/list", { state: { openTaskId: a.task } });
                        } else if (a.ntype === "task_inprogress") {
                          navigate("/task/assign/list", { state: { openTaskId: a.task } });
                        } else if (a.ntype === "task_comment_to_assigner" || a.ntype === "task_question") {
                          navigate("/task/assign/list", { state: { openTaskId: a.task } });

                        } else if (a.ntype === "task_comment_to_assignee" || a.ntype === "task_answer") {
                          navigate("/task/received/list", { state: { openTaskId: a.task } });
                        } else if (a.ntype === "approval_requested") {
                          navigate("/approval/inbox");
                        } else if (a.ntype === "approval_completed") {
                          navigate("/approval/done");
                        } else if (a.ntype === "approval_rejected") {
                          navigate("/approval/draftdocument/list");
                        }


                      }}



                      style={{
                        padding: "10px 14px", fontSize: "13px",
                        borderBottom: "1px solid #f0f0f0",
                        background: a.is_read ? "#fff" : "#f0fff4",
                        opacity: a.is_read ? 0.5 : 1,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{flex:1, marginRight:8, wordBreak:"keep-all"}}>{a.message}</div>
                        
                        <div style={{ display:"flex", flexDirection:"column", gap:3, flexShrink:0 }}>
                          {!a.is_read && (
                            <button


                              onClick={async (e) => {
                                e.stopPropagation();
                                if (a.source === "approval") {
                                  await api.patch(`/draft/approval-notifications/${a.id}/read/`);
                                } else {
                                  await api.patch(`/tasks/notifications/${a.id}/read/`);
                                }


                                setAlarms((prev) =>
                                  prev.map((n) => n.id === a.id && n.source === a.source ? { ...n, is_read: true } : n)
                                );
                                setUnreadCount((prev) => a.source === "task" ? Math.max(0, prev - 1) : prev);
                                setApprovalUnreadCount((prev) => a.source === "approval" ? Math.max(0, prev - 1) : prev);
                              }}



                              style={{
                                border:"1px solid #b3d9ff", background:"#f0f8ff",
                                borderRadius:4, padding:"2px 6px", fontSize:"10px",
                                color:"#0066cc", cursor:"pointer", whiteSpace:"nowrap", width:"36px"
                              }}
                            >
                              읽음
                            </button>
                          )}
                          <button


                            onClick={async (e) => {
                              e.stopPropagation();
                              if (a.source === "approval") {
                                await api.patch(`/draft/approval-notifications/${a.id}/hide/`);
                              } else {
                                await api.patch(`/tasks/notifications/${a.id}/hide/`);
                              }
                              setAlarms((prev) => prev.filter((n) => !(n.id === a.id && n.source === a.source)));
                            }}


                            style={{
                              border:"1px solid #ffcccc", background:"#fff5f5",
                              borderRadius:4, padding:"2px 6px", fontSize:"10px",
                              color:"#cc0000", cursor:"pointer", whiteSpace:"nowrap", width:"36px"
                            }}
                          >
                            삭제
                          </button>
                        </div>
                      </div>   

                      <div style={{ fontSize: "11px", color: "#aaa", marginTop: "3px" }}>
                        {a.created_at?.slice(0, 16).replace("T", " ")}
                      </div>
                    </div>  

                  ))}
                </div>
              )}
            </div>

            <span className={styles.divider}>|</span>

            <Link to="/mypage" className={styles.authLink}>
              마이페이지
            </Link>

            <span className={styles.divider}>|</span>

            <button
              type="button"
              className={styles.authLink}
              onClick={handleExtendSession}
            >
              로그인 연장
            </button>

            <span className={styles.divider}>|</span>

            <button
              type="button"
              className={styles.authLink}
              onClick={handleLogout}
            >
              로그아웃
            </button>
          </nav>
        ) : (
          <nav className={styles.authNav}>
            <Link to="/login" className={styles.authLink}>
              로그인
            </Link>
            <span className={styles.divider}>|</span>
            <Link to="/signup" className={styles.authLink}>
              회원가입
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}